import { useCallback, useEffect, useMemo, useRef } from 'react'
import { type FormInstance } from 'antd'
import type { JsonFormConfig } from '../components'
import { JsonForm } from '../components'
import {
  NodeType,
  type NodeComponent,
  NodeOperationTypes,
} from '@/features/nodes/base'
import { FLOW_DRAFT_LOCK_STATUS, useFlowDraftStore } from '@/store'
import { useNodeBranchRemoveConfirm } from '../hooks/useNodeBranchRemoveConfirm'

// import { LLMContextType } from '../llm/const'
import { LLMChannels } from '@/apis/llm/model'
import {
  BranchTypes,
  IntentForm,
  type IntentItem,
  LLMModelSelect,
  INTENT_MAX_CONTENT_SIZE,
} from './IntentForm'

export interface IntentNodeData {
  name: string
  conditions: IntentItem[]
  relation: {
    conditionResultId: string
    branchId?: string
    [o: string]: any
  }
  [k: string]: any
}

export const IntentNode: NodeComponent<IntentNodeData> = props => {
  const {
    data: { conditions },
    nodeElement,
    variables = [],
  } = props

  const formRef = useRef<FormInstance>()
  const addBranch = useFlowDraftStore(s => s.addBranch)
  const removeBranch = useFlowDraftStore(s => s.removeBranch)
  const lockStatus = useFlowDraftStore(s => s.lockStatus)

  const { beforeRemove, cancel } = useNodeBranchRemoveConfirm(props.id)

  const onAdd = useCallback(() => {
    const insertIndex = conditions.findIndex(
      item => item.type === BranchTypes.ELSE,
    )
    if (insertIndex > -1) {
      addBranch({
        id: props.id,
        conditionIndex: insertIndex,
        condition: {
          type: BranchTypes.ELSEIF,
          statement: '',
        },
      })
    }
  }, [conditions, props.id])

  const onDelete = useCallback(
    (id: string) => {
      removeBranch({
        id: props.id,
        conditionId: id,
      })
    },
    [props.id],
  )

  const List = useMemo<JsonFormConfig[]>(() => {
    return [
      {
        required: true,
        label: '分类模型',
        disabled: lockStatus !== FLOW_DRAFT_LOCK_STATUS.UNLOCK,
        name: ['inputs'],
        render: () => <LLMModelSelect nodeElement={nodeElement} />,
      },
      {
        label: '用户问题',
        disabled: lockStatus !== FLOW_DRAFT_LOCK_STATUS.UNLOCK,
        name: ['inputs', 'question'],
        required: true,
        type: 'TextEditor',
        rules: [{ required: true, message: '用户问题不能为空' }],
        componentProps: {
          placeholder:
            '输入需要识别意图的内容或者问题，如：“BetterYeah AI 的意图识别是怎么做的？”',
          variables,
          variableTipsContainer: nodeElement,
        },
      },
      {
        label: '背景知识',
        disabled: lockStatus !== FLOW_DRAFT_LOCK_STATUS.UNLOCK,
        name: ['inputs', 'background'],
        type: 'TextEditor',
        tooltip:
          '当分类不准时，输入分类的规则说明和背景知识，帮助AI知道如何更准确地分类',
        componentProps: {
          placeholder: '输入背景知识',
          variables,
          variableTipsContainer: nodeElement,
        },
      },
      {
        name: 'conditions',
        label: '意图分类',
        required: true,
        // tooltip:
        //   '当分类不准时，输入分类的规则说明和背景知识，帮助AI知道如何更准确地分类',
        rules: [
          {
            required: true,
            validator: (_, value: IntentItem[]) => {
              const hasEmptyStatement = value
                .filter(v => v.type !== BranchTypes.ELSE)
                .some(item => item.statement === '')

              const hasLongStatement = value
                .filter(v => v.type !== BranchTypes.ELSE)
                .some(item => item.statement?.length >= INTENT_MAX_CONTENT_SIZE)

              if (hasLongStatement) {
                return Promise.reject(
                  new Error(
                    `请检查意图分类，不能超过${INTENT_MAX_CONTENT_SIZE}字`,
                  ),
                )
              }

              if (hasEmptyStatement) {
                return Promise.reject(new Error('请检查意图分类，不能为空'))
              }

              return Promise.resolve()
            },
          },
        ],
        render: () => {
          return (
            <IntentForm
              variables={props.variables}
              variableTipsContainer={props.nodeElement}
              onAdd={onAdd}
              onDelete={onDelete}
              onBeforeRemove={beforeRemove}
              onCancel={cancel}
              disabled={lockStatus !== FLOW_DRAFT_LOCK_STATUS.UNLOCK}
            />
          )
        },
      },
    ]
  }, [props.nodeElement, props.variables, onAdd, onDelete, lockStatus])

  useEffect(() => {
    if (formRef.current) {
      const fieldValue = formRef.current.getFieldValue('conditions')
      // 当删除或增减的时候立即更新表单值
      if (fieldValue?.length !== props.data.conditions.length) {
        formRef.current.setFieldValue('conditions', props.data.conditions)
      }
    }
  }, [props.data.conditions, formRef.current])

  return useMemo(
    () => (
      <div className='w-420px p-16'>
        <JsonForm
          ref={formRef}
          list={List}
          disabled={lockStatus !== FLOW_DRAFT_LOCK_STATUS.UNLOCK}
        />
      </div>
    ),
    [formRef, List, lockStatus],
  )
}

IntentNode.meta = {
  type: NodeType.INTENT,
  operationType: NodeOperationTypes.MULTI_BRANCH_NODE,
  actionType: 'INTENT',
  typeName: '意图识别',
  icon: 'yitufenleijiedian',
  description: '可以对用户问题进行意图分类',
  backgroundColor: '#AA50F9',
  customSourceHandle: true,
  initialData: {
    name: 'intent_1',
    relation: {
      conditionResultId: '',
    },
    inputs: {
      model: 'gpt-4o-mini',
      channel: LLMChannels.GPT,
      question: '',
      background: '',
    },

    conditions: [
      {
        type: BranchTypes.IF,
        statement: '',
      },
      {
        type: BranchTypes.ELSE,
        statement: '',
      },
    ],
  },
  classNames: ['overflow-visible'],
}
